import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Wind, Sparkles, Heart, Zap, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { companyInfo } from '../data/mock';

const AirDuctCleaningPage = () => {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: Sparkles,
      title: "Thorough Cleaning",
      description: "Our skilled technicians utilize advanced equipment to ensure your air ducts are meticulously cleaned, reducing airborne pollutants and improving air circulation."
    },
    {
      icon: Heart,
      title: "Healthier Home Environment",
      description: "Regular air duct cleaning helps eliminate allergens, mold spores, and dust mites, contributing to better indoor air quality and overall health."
    },
    {
      icon: Zap,
      title: "Increased Energy Efficiency",
      description: "Clean air ducts allow your HVAC system to operate more efficiently, potentially lowering your energy bills while providing consistent heating and cooling."
    },
    {
      icon: Award,
      title: "Professional Service",
      description: "We pride ourselves on delivering exceptional customer service, ensuring your satisfaction with every job."
    }
  ];

  return (
    <div className="min-h-screen bg-[#FDFCF7]">


      {/* Navigation Bar */}
      <div className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#622B62] hover:text-[#DA9C3A] transition-colors font-medium"
          >
            <ArrowLeft size={20} />
            Back to Home
          </button>
          <a href={`tel:${companyInfo.phone}`}>
            <Button className="bg-[#DA9C3A] hover:bg-[#C48A2A] text-white font-semibold px-5 py-2 rounded-full">
              <Phone size={16} className="mr-2" />
              {companyInfo.phone}
            </Button>
          </a>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-[#622B62] text-white py-16 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#7A3D7A] rounded-full -translate-y-1/2 translate-x-1/2 opacity-50" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#4A1F4A] rounded-full translate-y-1/2 -translate-x-1/2 opacity-50" />
        
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <div className="w-20 h-20 rounded-full bg-[#DA9C3A] flex items-center justify-center mx-auto mb-6">
            <Wind size={40} />
          </div>
          <h1 className="romano-heading text-4xl sm:text-5xl font-bold mb-4">
            Air Duct Cleaning Services
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Enhance Your Indoor Air Quality with Professional Air Duct Cleaning
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 py-16">
        {/* Introduction */}
        <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-lg border border-gray-100 mb-12">
          <p className="text-lg text-gray-700 leading-relaxed">
            Breathe easier and improve your home's indoor air quality with our expert air duct cleaning services at Romano Heating and Cooling. Our state-of-the-art equipment effectively removes dirt, dust, allergens, and debris from your air ducts, promoting a healthier living environment for you and your family.
          </p>
        </div>

        {/* Why Choose Section */}
        <div className="text-center mb-12">
          <span className="romano-badge mb-4 inline-block">Our Promise</span>
          <h2 className="romano-heading text-3xl sm:text-4xl font-bold text-[#622B62] mb-4">
            Why Choose Our Air Duct Cleaning?
          </h2>
          <div className="section-divider mx-auto" />
        </div>

        {/* Benefits Grid */}
        <div className="grid sm:grid-cols-2 gap-8 mb-16">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow"
            >
              <div className="w-14 h-14 rounded-xl bg-[#622B62] flex items-center justify-center mb-5">
                <benefit.icon size={26} className="text-[#DA9C3A]" />
              </div>
              <h3 className="text-xl font-bold text-[#622B62] mb-3">{benefit.title}</h3>
              <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-[#622B62] to-[#4A1F4A] rounded-3xl p-10 text-center text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative z-10">
            <Wind size={48} className="text-[#DA9C3A] mx-auto mb-6" />
            <p className="text-xl sm:text-2xl font-medium mb-8 max-w-3xl mx-auto leading-relaxed">
              Don't compromise on the air quality in your home. Schedule your air duct cleaning service today and experience the difference in your indoor environment!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${companyInfo.phone}`}>
                <Button className="bg-[#DA9C3A] hover:bg-[#C48A2A] text-white font-bold px-10 py-6 text-lg rounded-full shadow-xl">
                  <Phone size={22} className="mr-2" />
                  Call Now: {companyInfo.phone}
                </Button>
              </a>
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#622B62] font-bold px-10 py-6 text-lg rounded-full"
                onClick={() => navigate('/#contact')}
              >
                Request a Quote
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#622B62] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img
              src={companyInfo.logo}
              alt={companyInfo.name}
              className="h-24 w-24 object-contain rounded-lg"
            />
            <div className="text-left">
              <h3 className="font-bold romano-heading">Romano</h3>
              <p className="text-white/70 text-sm">Heating & Cooling</p>
            </div>
          </div>
          <p className="text-white/60 text-sm">
            &copy; {new Date().getFullYear()} Romano Heating and Cooling. All Rights Reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AirDuctCleaningPage;
